import type { PropsWithChildren } from 'react';
import { Children } from 'react';
import type { Settings } from 'react-slick';
import Slider from 'react-slick';
import cx from 'classnames';

import VehicleCard from 'Components/shared/VehicleCards/VehicleCard/VehicleCard';
import VehicleCardLoadingView from 'Components/shared/VehicleCards/VehicleCard/VehicleCardLoadingView/VehicleCardLoadingView';

import type { SimilarVehiclesEvents } from '../SimilarVehicles';

import { useSimilarVehiclesCarousel } from './useSimilarVehiclesCarousel';

import 'slick-carousel/slick/slick.css';
import 'slick-carousel/slick/slick-theme.css';
import styles from './SimilarVehiclesCarousel.module.scss';

export const SimilarVehiclesCarousel: React.FC<
	PropsWithChildren<{
		customLoadingView?: React.ReactNode;
		isLoading: boolean;
		onNextArrowClick: SimilarVehiclesEvents['onNextArrowClick'];
		onPrevArrowClick: SimilarVehiclesEvents['onPrevArrowClick'];
		sliderSettings?: Settings;
		vehicleCardClassName?: string;
	}>
> = ({
	children,
	customLoadingView,
	isLoading,
	onNextArrowClick,
	onPrevArrowClick,
	sliderSettings: sliderSettingsParam,
	vehicleCardClassName,
}) => {
	const { sliderSettings } = useSimilarVehiclesCarousel({
		childrenCount: Children.count(children),
		isLoading,
		onNextArrowClick,
		onPrevArrowClick,
		settings: sliderSettingsParam,
	});
	const slidesToShow = Math.ceil(sliderSettings.slidesToShow ?? 1);

	const childrenArray = Children.toArray(children);
	/*
	 * If there are fewer children than slidesToShow, we need to add placeholders to fill the carousel.
	 * This is because the carousel will not render the slides in the correct positions if there are
	 * fewer slides than slidesToShow.
	 */
	const placeholdersArray = Array(Math.max(slidesToShow - childrenArray.length, 0))
		.fill(0)
		.map(() => null);
	const loadingCardsArray = Array(slidesToShow)
		.fill(0)
		.map((index) => (
			<VehicleCard
				key={index}
				className={vehicleCardClassName}
				loading={true}
				loadingView={customLoadingView ?? <VehicleCardLoadingView />}
			/>
		));
	const slidesArray = isLoading ? loadingCardsArray : [...childrenArray, ...placeholdersArray];

	return (
		<div className={styles.wrapper} data-testid={isLoading ? 'loadingVehicleCards' : undefined}>
			<Slider
				{...sliderSettings}
				className={cx(styles.carousel, {
					[styles.withDots]: sliderSettings.dots,
					[styles.hideDefaultNextArrow]: sliderSettings.hideDefaultNextArrow,
					[styles.hideDefaultPrevArrow]: sliderSettings.hideDefaultPrevArrow,
					[styles.centerItems]: sliderSettings.slidesToShow === 1,
					[styles.showOverflow]: sliderSettings.showOverflow,
				})}
			>
				{slidesArray.map((slide, index) => (
					<div key={index} className={styles.slide}>
						{slide}
					</div>
				))}
			</Slider>
		</div>
	);
};
